/**
 * Axios HTTP client — base URL configuration, JWT injection, CSRF
 * double-submit cookie, 401 token refresh, and 429 rate limit interceptors.
 *
 * @module api/client
 * @see TASK-FRONTEND-CHAT-API-CHG-001
 */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import {
  ACT_AS_HEADER,
  IMPERSONATION_TERMINAL_EVENT,
  getActAsSessionRef,
  readImpersonationTerminalReason,
  type ImpersonationTerminalEventDetail,
} from "./impersonation";
import { dispatchTenantRefused, tenantRefusalFromBody } from "./tenantAccess";

// ---------------------------------------------------------------------------
// Per-request flags (TASK-IMP-AUTHCONTEXT-SPA-WIRING-CHG-009)
// ---------------------------------------------------------------------------

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Omit `X-Verisa-Act-As` on THIS request. Set by the self-exit
     * (SPEC-FRONTEND-RULE-095(9)): the client-side convenience half; the
     * server carve-out of SPEC-GO-API-CONTRACT-039 is the guarantee.
     */
    skipActAs?: boolean;
    /**
     * Never refresh-and-retry THIS request on a 401. Set by a teardown-path
     * call whose token may already be dead, so it cannot re-enter the refresh
     * backoff loop it is running inside.
     */
    skipAuthRefresh?: boolean;
  }
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Base URL pulled from Vite env; falls back to current origin + /api/v1. */
export const BASE_URL: string = import.meta.env.VITE_API_BASE_URL || "/api/v1";

// ---------------------------------------------------------------------------
// Token helpers (thin wrappers so auth module can replace them)
// ---------------------------------------------------------------------------

let _getAccessToken: () => string | null = () => null;

let _refreshAccessToken: () => Promise<string | null> = async () => null;

/**
 * Listeners told that the registered token getter was swapped. The auth module
 * calls {@link setTokenGetter} on every token change - login, each refresh,
 * logout - so a swap is the one moment a module mounted ABOVE the auth
 * provider can learn that authentication has settled without importing it
 * (SPEC-PLATFORM-BRANDING RULE-019 tier (2), D-BR-28; the BrandingProvider is
 * the consumer). A listener reads the token through {@link getAccessToken}.
 */
const _tokenListeners = new Set<() => void>();

/** Register a custom token getter (called by auth module on init). */
export function setTokenGetter(fn: () => string | null): void {
  _getAccessToken = fn;
  // AFTER the swap, so a listener's getAccessToken() sees the new getter. A
  // snapshot, so a listener that unsubscribes itself does not skip another.
  for (const listener of Array.from(_tokenListeners)) {
    try {
      listener();
    } catch {
      // One faulty listener must neither stop the others nor make the auth
      // module's registration throw - the token getter is already swapped.
    }
  }
}

/**
 * Be told each time the token getter is swapped. Returns the unsubscribe
 * function. Existing callers of the getter are unaffected.
 */
export function subscribeAccessToken(listener: () => void): () => void {
  _tokenListeners.add(listener);
  return () => {
    _tokenListeners.delete(listener);
  };
}

/** Return the current access token via the registered getter. */
export function getAccessToken(): string | null {
  return _getAccessToken();
}

/** Register a custom refresh function (called by auth module on init). */
export function setTokenRefresher(fn: () => Promise<string | null>): void {
  _refreshAccessToken = fn;
}

/** Trigger a token refresh via the registered refresher. */
export function refreshAccessToken(): Promise<string | null> {
  return _refreshAccessToken();
}

// ---------------------------------------------------------------------------
// Tenant / Workspace header helpers
// ---------------------------------------------------------------------------

let _getTenantId: () => string | null = () => null;
let _getWorkspaceId: () => string | null = () => null;

/** Register a tenant ID getter (called by AuthContext on init). */
export function setTenantIdGetter(fn: () => string | null): void {
  _getTenantId = fn;
}

/** Register a workspace ID getter (called by WorkspaceContext on init). */
export function setWorkspaceIdGetter(fn: () => string | null): void {
  _getWorkspaceId = fn;
}

/** Return the current tenant ID via the registered getter. */
export function getTenantId(): string | null {
  return _getTenantId();
}

/** Return the current workspace ID via the registered getter. */
export function getWorkspaceId(): string | null {
  return _getWorkspaceId();
}

// ---------------------------------------------------------------------------
// Rate-limit event
// ---------------------------------------------------------------------------

export type RateLimitHandler = (retryAfterSec: number) => void;
let _onRateLimit: RateLimitHandler = () => {
  /* no-op by default */
};

/** Register a handler invoked when a 429 response is received. */
export function setRateLimitHandler(fn: RateLimitHandler): void {
  _onRateLimit = fn;
}

// ---------------------------------------------------------------------------
// Response-error observers
// ---------------------------------------------------------------------------

/**
 * An observer told about every failed response, BEFORE the error is rejected to
 * the caller. It observes only: it cannot change, retry or swallow the error.
 * The one registrant today is the surface-refusal handler
 * (`surfaces/surfaceRefusal.ts`, SPEC-TENANT-SURFACES RULE-015), which turns a
 * 403 `surface_not_available` into a cache invalidation and a not-found signal.
 */
export type ResponseErrorObserver = (error: AxiosError) => void;

const _responseErrorObservers = new Set<ResponseErrorObserver>();

/** Observe every failed response. Returns the unsubscribe function. */
export function subscribeResponseError(observer: ResponseErrorObserver): () => void {
  _responseErrorObservers.add(observer);
  return () => {
    _responseErrorObservers.delete(observer);
  };
}

function notifyResponseErrorObservers(error: AxiosError): void {
  for (const observer of Array.from(_responseErrorObservers)) {
    try {
      observer(error);
    } catch (err) {
      // An observer must never change what the caller receives.
      console.warn("[api/client] response-error observer threw:", err);
    }
  }
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

console.log("[api/client] BASE_URL:", BASE_URL);

const client: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 120_000, // 120s — matches backend WriteTimeout for LLM inference
});

// ---------------------------------------------------------------------------
// Request interceptor: inject JWT bearer token
// ---------------------------------------------------------------------------

client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = _getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Request interceptor: CSRF double-submit cookie
// ---------------------------------------------------------------------------

/** Methods that mutate state and require CSRF protection. */
const CSRF_METHODS = new Set(["post", "put", "patch", "delete"]);

/**
 * Parse a cookie value from `document.cookie` by name.
 * Returns an empty string when the cookie is absent or in non-browser envs.
 */
function getCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : "";
}

client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const method = (config.method ?? "get").toLowerCase();
    if (CSRF_METHODS.has(method)) {
      const csrfToken = getCookie("XSRF-TOKEN");
      if (csrfToken) {
        config.headers["X-XSRF-TOKEN"] = csrfToken;
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Request interceptor: inject X-Tenant-ID and X-Workspace-ID headers
// ---------------------------------------------------------------------------

client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const tenantId = _getTenantId();
    if (tenantId) {
      config.headers["X-Tenant-ID"] = tenantId;
    }
    const workspaceId = _getWorkspaceId();
    if (workspaceId) {
      config.headers["X-Workspace-ID"] = workspaceId;
    }
    // Lane (A)1 of SPEC-FRONTEND-RULE-095(6): the act-as stamp, beside the
    // tenant and workspace stamps and fed by the same kind of synchronous
    // getter. The per-request opt-out is honoured, and a stale header from a
    // re-sent config is removed rather than trusted.
    const actAs = config.skipActAs ? null : getActAsSessionRef();
    if (actAs) {
      config.headers[ACT_AS_HEADER] = actAs;
    } else {
      delete config.headers[ACT_AS_HEADER];
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Request interceptor: X-Correlation-ID for end-to-end request tracing
// ---------------------------------------------------------------------------

client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.headers["X-Correlation-ID"] =
      crypto.randomUUID?.() ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Debug event reporter (fire-and-forget to /api/admin/debug/events)
// ---------------------------------------------------------------------------

/** Separate axios instance to avoid interceptor loops.
 *  Debug endpoints live at /api/admin/debug/* (no /v1/ prefix).
 *
 *  THE ONE EXEMPTION from the act-as stamp (SPEC-FRONTEND-RULE-095(6)(C)):
 *  /api/admin/debug/events is client telemetry, not an action, so it reports
 *  as the impersonator - the correct actor for a client-side error - and is
 *  never stamped. It has no interceptors and its headers below are built by
 *  hand WITHOUT `X-Verisa-Act-As`; stamping it would turn every API error on an
 *  impersonated page into a refused POST plus a durable impersonate_rejected
 *  row. */
const debugClient = axios.create({ baseURL: "/api", timeout: 3000 });

interface ApiErrorResponse {
  error?: string;
  message?: string;
  request_id?: string;
}

function reportDebugEvent(error: AxiosError): void {
  try {
    const apiErr = error.response?.data as ApiErrorResponse | undefined;
    const token = _getAccessToken();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    debugClient
      .post(
        "/admin/debug/events",
        {
          kind: "api_error",
          service: "frontend",
          method: error.config?.method?.toUpperCase() ?? "",
          path: error.config?.url ?? "",
          status: error.response?.status ?? 0,
          error_code: apiErr?.error ?? "",
          message: apiErr?.message ?? error.message,
          request_id: apiErr?.request_id ?? "",
          details: {
            correlation_id:
              error.config?.headers?.["X-Correlation-ID"] ?? "",
          },
        },
        { headers },
      )
      .catch((err) => {
        // Debug reporting must never break the app, but log so self-reporting outages are visible.
        console.warn("[api/client] Debug event publish failed:", err);
      });
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Response interceptor: 401 auto-refresh & 429 rate-limit
// ---------------------------------------------------------------------------

/**
 * Detect a "no refresh token" first-paint 401 — the gateway rejected the
 * original request because there was never an authenticated session (no
 * HttpOnly refresh cookie), not because a real session was revoked. Such a
 * 401 must NOT escalate to a logout (RULE-013). Matches the gateway's
 * `error: "no_refresh_token"` / `"no refresh token"` body.
 */
function isNoRefreshTokenError(error: AxiosError): boolean {
  const data = error.response?.data as ApiErrorResponse | undefined;
  const code = (data?.error ?? data?.message ?? "").toLowerCase();
  return code.includes("no_refresh_token") || code.includes("no refresh token");
}

let isRefreshing = false;

interface RefreshSubscriber {
  resolve: (value: AxiosResponse | PromiseLike<AxiosResponse>) => void;
  reject: (error: unknown) => void;
  config: InternalAxiosRequestConfig;
}

let refreshSubscribers: RefreshSubscriber[] = [];

function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach(({ resolve, config }) => {
    config.headers.Authorization = `Bearer ${token}`;
    resolve(client(config));
  });
  refreshSubscribers = [];
}

function onTokenRefreshFailed(error: unknown): void {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
}

client.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`[api/client] ${response.config.method?.toUpperCase()} ${response.config.url} → ${response.status}`);
    return response;
  },
  async (error: AxiosError) => {
    // THE TERMINAL IMPERSONATION BRANCH - THE FIRST STATEMENT, AND ITS POSITION
    // IS THE REQUIREMENT (SPEC-FRONTEND-RULE-095(7), a named exception to
    // RULE-005's automatic refresh). It sits ABOVE reportDebugEvent: a branch
    // copied to the /public/ branch's position below would still fire the
    // fire-and-forget telemetry POST. On a closed terminal code it rejects at
    // once - no refresh (the credential refreshed would be the impersonator's
    // own, so it would SUCCEED), no retry (re-stamped, it would be refused
    // again) and so no retry-exhaustion forceLogout that signs the impersonator
    // out for a reason unrelated to them. The shell renders the terminal card.
    const terminalReason = readImpersonationTerminalReason(error);
    if (terminalReason) {
      const detail: ImpersonationTerminalEventDetail = { reason: terminalReason };
      window.dispatchEvent(new CustomEvent(IMPERSONATION_TERMINAL_EVENT, { detail }));
      return Promise.reject(error);
    }

    // THE TENANT REFUSAL (R-MT-08): the signed-in person's tenant is suspended
    // or gone. Terminal like the branch above - a refresh would be refused the
    // same way - so AuthContext signs the person out and the login page says why.
    const tenantRefusal = tenantRefusalFromBody(error.response?.data);
    if (tenantRefusal) {
      dispatchTenantRefused(tenantRefusal);
      return Promise.reject(error);
    }

    console.error(`[api/client] ${error.config?.method?.toUpperCase()} ${error.config?.url} → ${error.response?.status ?? "NETWORK_ERROR"}`, error.message);

    reportDebugEvent(error);
    notifyResponseErrorObservers(error);

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // A teardown-path call (the self-exit issued from logout / forceLogout)
    // must never refresh-and-retry: its token may be the dead one that started
    // the teardown.
    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    // SPEC-FRONTEND-RULE-075: on the ANONYMOUS public lane (POST /public/chat,
    // channel principal) a 401 means an unknown or retired PUBLIC WIDGET TOKEN and
    // is TERMINAL. There is no session to refresh, so attempting one is not merely
    // useless: it relabels a dead token as an expired session, which is exactly how
    // the 2026-09-11 prod demo failure was misdiagnosed. Reject immediately.
    const isAnonymousPublicLane = (originalRequest.url ?? "").includes("/public/");
    if (error.response?.status === 401 && isAnonymousPublicLane) {
      return Promise.reject(error);
    }

    // --- 401: Attempt token refresh once -----------------------------------
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          refreshSubscribers.push({ resolve, reject, config: originalRequest });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await _refreshAccessToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          onTokenRefreshed(newToken);
          return client(originalRequest);
        }
        // Server rejected refresh (4xx invalid_grant / no refresh cookie) —
        // drain all queued subscribers. A "no refresh token" first-paint 401
        // (no prior authenticated state) must NOT escalate to a logout, so
        // flag it for AuthContext to suppress (RULE-013).
        onTokenRefreshFailed(error);
        window.dispatchEvent(new CustomEvent('auth:session-expired', {
          detail: {
            reason: 'server-rejection',
            error,
            statusCode: error.response?.status ?? 401,
            noRefreshToken: isNoRefreshTokenError(error),
          },
        }));
      } catch (refreshError) {
        // Thrown from the refresher: gateway unreachable / 5xx / 503
        // (TASK-AUTH-REFRESH-503-SIGNAL) — a TRANSIENT failure. Retry once after
        // 1s backoff, then hand off to the network-error bounded-retry path.
        try {
          await new Promise((r) => setTimeout(r, 1000));
          const retryToken = await _refreshAccessToken();
          if (retryToken) {
            originalRequest.headers.Authorization = `Bearer ${retryToken}`;
            onTokenRefreshed(retryToken);
            return client(originalRequest);
          }
          // Retry returned null = server now rejects (4xx). Treat as
          // server-rejection so the grace/backoff policy decides.
          onTokenRefreshFailed(error);
          window.dispatchEvent(new CustomEvent('auth:session-expired', {
            detail: {
              reason: 'server-rejection',
              error,
              statusCode: error.response?.status ?? 401,
              noRefreshToken: isNoRefreshTokenError(error),
            },
          }));
        } catch (retryError) {
          onTokenRefreshFailed(retryError);
          window.dispatchEvent(new CustomEvent('auth:session-expired', {
            detail: { reason: 'network-error', error: retryError, statusCode: 0 },
          }));
        }
      } finally {
        isRefreshing = false;
      }
    }

    // --- 429: Extract retry-after and notify handler -----------------------
    if (error.response?.status === 429) {
      const retryAfter = Number(
        error.response.headers["retry-after"] ?? "60",
      );
      _onRateLimit(retryAfter);
    }

    return Promise.reject(error);
  },
);

export default client;
